import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  api, type AdminHealth, type AdminUser, type AuditEntry, type Stream,
} from '../api/client.ts';
import { t, type StringKey } from '../i18n/index.ts';
import { formatBytes, formatClockSeconds, formatDuration, formatRemaining } from '../lib/format.ts';

const ROLES = ['viewer', 'editor', 'admin'] as const;
const ROLE_LABEL: Record<string, StringKey> = {
  viewer: 'admin.role_viewer',
  editor: 'admin.role_editor',
  admin: 'admin.role_admin',
};

/** Health is worth a fresh look while someone is watching it. */
const REFRESH_MS = 15_000;

interface Props {
  timeZone: string;
  streams: Stream[];
  onStreamsChanged: () => void;
}

/**
 * The admin screen (spec §10.2 screen 6): system health, streams, users and the
 * audit log.
 *
 * Read-mostly on purpose. The things that can be changed here are the ones an
 * operator legitimately changes — retention, who has an account, what they may
 * do — and each is written straight through to the same endpoints the rest of
 * the product uses, so nothing has a second path into the data.
 */
export function AdminScreen({ timeZone, streams, onStreamsChanged }: Props): React.ReactElement {
  const [health, setHealth] = useState<AdminHealth | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [h, u, a] = await Promise.all([
        api.adminHealth(), api.adminUsers(), api.adminAudit(),
      ]);
      setHealth(h);
      setUsers(u);
      setAudit(a);
      setError(null);
    } catch {
      setError(t('error.network'));
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (error && health === null) return <div className="empty">{error}</div>;
  if (health === null) return <div className="empty">{t('player.loading')}</div>;

  const diskLow = health.disk.free_percent < health.disk.floor_percent;

  return (
    <section className="admin">
      <div className="grid-head"><h2>{t('admin.health')}</h2></div>

      <div className="admin-cards">
        <div className="admin-card">
          <span className="label">{t('admin.disk')}</span>
          <div className="gauge" role="img"
            aria-label={`${health.disk.free_percent}%`}>
            <div
              className={`gauge-fill ${diskLow ? 'low' : ''}`}
              style={{ width: `${100 - health.disk.free_percent}%` }}
            />
          </div>
          <span className="m admin-value" dir="ltr">
            {t('admin.disk_free', {
              percent: health.disk.free_percent,
              total: formatBytes(health.disk.total_bytes),
            })}
          </span>
          <span className={`admin-note ${diskLow ? 'warn' : ''}`}>
            {t('admin.disk_floor', { percent: health.disk.floor_percent })}
          </span>
        </div>

        {health.streams.map((s) => (
          <div className="admin-card" key={s.id}>
            <span className="label">{s.slug}</span>
            <span className="admin-value">{s.name}</span>
            <dl className="admin-facts">
              <dt>{t('admin.last_seen')}</dt>
              <dd className="m" dir="ltr">
                {s.last_seen_age_seconds === null
                  ? '—'
                  : formatDuration(s.last_seen_age_seconds)}
              </dd>
              <dt>{t('admin.restarts')}</dt>
              <dd className="m" dir="ltr">{s.recorder_restarts}</dd>
              <dt>{t('stream.archive_size')}</dt>
              <dd className="m" dir="ltr">{formatBytes(s.archive_bytes)}</dd>
              <dt>{t('admin.purge_lag')}</dt>
              <dd className="m" dir="ltr">
                {s.purge_lag_seconds > 0 ? formatDuration(s.purge_lag_seconds) : '—'}
              </dd>
            </dl>
          </div>
        ))}

        <div className="admin-card">
          <span className="label">{t('admin.jobs')}</span>
          <dl className="admin-facts">
            {health.jobs.map((j) => (
              <div key={`${j.kind}-${j.status}`} style={{ display: 'contents' }}>
                <dt className="m" dir="ltr">{j.kind} · {j.status}</dt>
                <dd className="m" dir="ltr">{j.count}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <StreamSettings streams={streams} onChanged={onStreamsChanged} />
      <UserSettings users={users ?? []} timeZone={timeZone} onChanged={() => void load()} />

      <div className="grid-head" style={{ marginTop: 28 }}><h2>{t('admin.audit')}</h2></div>
      <div className="table-scroll">
        <table className="exports">
          <thead>
            <tr>
              <th>{t('admin.when')}</th>
              <th>{t('admin.actor')}</th>
              <th>{t('admin.action')}</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {(audit ?? []).map((row) => (
              <tr key={row.id}>
                <td className="m" dir="ltr">{formatClockSeconds(row.created_at, timeZone)}</td>
                <td>{row.actor ?? <span className="muted">—</span>}</td>
                <td className="m" dir="ltr">{row.action}</td>
                <td className="m muted" dir="ltr">{row.ip ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function StreamSettings({
  streams, onChanged,
}: { streams: Stream[]; onChanged: () => void }): React.ReactElement {
  const [saving, setSaving] = useState<number | null>(null);

  return (
    <>
      <div className="grid-head" style={{ marginTop: 28 }}><h2>{t('admin.streams')}</h2></div>
      <div className="admin-rows">
        {streams.map((stream) => (
          <div className="admin-row" key={stream.id}>
            <span className="admin-value">{stream.name}</span>
            <span className="m muted" dir="ltr">{stream.slug}</span>
            <label className="field-inline">
              <span className="label">{t('admin.retention_hours')}</span>
              <input
                className="m"
                dir="ltr"
                type="number"
                min={1}
                defaultValue={stream.retention_hours}
                style={{ width: 90 }}
                onBlur={(e) => {
                  const hours = Number(e.target.value);
                  if (!Number.isInteger(hours) || hours < 1 || hours === stream.retention_hours) return;
                  setSaving(stream.id);
                  // Takes effect on the retention worker's next pass, which
                  // re-syncs expiries from the stream (D11).
                  void api.updateStream(stream.id, { retention_hours: hours })
                    .then(onChanged)
                    .finally(() => setSaving(null));
                }}
              />
            </label>
            {saving === stream.id && <span className="admin-note">{t('admin.saved')}</span>}
          </div>
        ))}
      </div>
    </>
  );
}

function UserSettings({
  users, timeZone, onChanged,
}: { users: AdminUser[]; timeZone: string; onChanged: () => void }): React.ReactElement {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<string>('viewer');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function add(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setFormError(null);
    try {
      await api.createUser({ email, display_name: name, password, role });
      setEmail(''); setName(''); setPassword(''); setRole('viewer');
      onChanged();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : t('error.unknown'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="grid-head" style={{ marginTop: 28 }}><h2>{t('admin.users')}</h2></div>

      <div className="table-scroll">
        <table className="exports">
          <thead>
            <tr>
              <th>{t('admin.name')}</th>
              <th>{t('auth.email')}</th>
              <th>{t('admin.role')}</th>
              <th>{t('admin.last_login')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.display_name}</td>
                <td className="m" dir="ltr">{user.email}</td>
                <td>
                  <select
                    value={user.role}
                    dir="ltr"
                    onChange={(e) => void api.updateUser(user.id, { role: e.target.value }).then(onChanged)}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>{t(ROLE_LABEL[r]!)}</option>
                    ))}
                  </select>
                </td>
                <td className="m" dir="ltr">
                  {user.last_login_at
                    ? formatClockSeconds(user.last_login_at, timeZone)
                    : <span className="muted">{t('admin.never')}</span>}
                </td>
                <td>
                  <button
                    type="button"
                    onClick={() =>
                      void api.updateUser(user.id, { is_active: !user.is_active }).then(onChanged)}
                  >
                    {user.is_active ? t('admin.deactivate') : t('admin.activate')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form className="admin-row" onSubmit={add} style={{ marginTop: 12 }}>
        <label className="field-inline">
          <span className="label">{t('admin.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required style={{ width: 150 }} />
        </label>
        <label className="field-inline">
          <span className="label">{t('auth.email')}</span>
          <input type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} required style={{ width: 200 }} />
        </label>
        <label className="field-inline">
          <span className="label">{t('admin.password')}</span>
          <input type="password" dir="ltr" value={password} onChange={(e) => setPassword(e.target.value)} required style={{ width: 170 }} />
        </label>
        <label className="field-inline">
          <span className="label">{t('admin.role')}</span>
          <select value={role} dir="ltr" onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r]!)}</option>)}
          </select>
        </label>
        <button type="submit" className="cta" disabled={busy}>{t('admin.add_user')}</button>
        <span className="admin-note">{t('admin.password_hint')}</span>
        {formError && <span className="admin-note warn" role="alert">{formError}</span>}
      </form>
    </>
  );
}
